import React from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Autocomplete,
  TextField,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { resolveTaskUidForTask } from "../../../../AllEquipments/CommonFeatures/DCSTab/Table/Dashboard/docListAPI";
import { buildResourceColumn } from "../../../../AllEquipments/CommonFeatures/Resources/buildResourceColumn";
import { buildSiColumn } from "../../../../AllEquipments/CommonFeatures/MCS3/Schedule4D/SiTableCell";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";

const getOrdinalSuffix = (number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = number % 100;
  return number + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

const convertDateString = (dateString) => {
  const dateObject = new Date(dateString);
  const formattedDate = `${dateObject.getDate()}-${dateObject.toLocaleString(
    "en-US",
    { month: "short" },
  )}-${dateObject.getFullYear()}`;
  return <p>{formattedDate}</p>;
};

const statusColorMap = {
  "Not Started": "default",
  Progress: "primary",
  Hold: "warning",
  Completed: "success",
  Delayed: "error",
};

export const taskListColumns = (
  plant_people,
  handleButtonClick,
  currentUser,
  plantLeads,
  handleCommentDialogOpen,
  tasksData = [],
  fixedType = null,
  mrqList = [],
  materials = [],
  isPCSTab,
  documentList = [],
  resources = [],
  onSiChange,
  siUpdatingIds,
) => {
  console.log("task list columns are", isPCSTab);
  // Extract unique task codes from existing tasks
  const existingTaskCodes = (() => {
    if (!Array.isArray(tasksData)) return [];
    const codes = tasksData
      .map((task) => task.taskCode)
      .filter((code) => code && code.trim() !== "")
      .filter((code, index, self) => self.indexOf(code) === index); // Get unique values
    return codes.sort();
  })();

  const mrqTaskCodes = (() => {
    if (!Array.isArray(mrqList)) return [];
    const codes = mrqList
      .map((mrq) => mrq?.mrqNumber)
      .filter((code) => code && String(code).trim() !== "");
    return codes;
  })();

  const taskCodeOptions = Array.from(
    new Set([...existingTaskCodes, ...mrqTaskCodes]),
  ).sort();

  const afterPrgressColumns = [
    {
      accessorKey: "status",
      header: `Status`,
      enableColumnFilter: false,
      size: 80,
      // Status should be editable
      enableEditing: true,
      muiEditTextFieldProps: () => ({
        children: [
          "Not Started",
          "Progress",
          "Hold",
          "Completed",
          "Delayed",
        ].map((func) => (
          <MenuItem key={func} value={func}>
            {func}
          </MenuItem>
        )),
        select: true,
      }),
      Cell: ({ cell }) => (
        <>
          {cell.getValue() ? (
            <Chip
              label={cell.getValue()}
              size="small"
              color={statusColorMap[cell.getValue()] || "default"}
            />
          ) : (
            <></>
          )}
        </>
      ),
    },
    {
      accessorKey: "chat",
      header: `Chat`,
      enableEditing: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 50,
      Header: () => <ChatIcon />,
      Cell: ({ row }) => (
        <IconButton onClick={() => handleCommentDialogOpen(row)}>
          <ChatIcon />
        </IconButton>
      ),
    },
    {
      accessorKey: "attachment",
      header: `Attachment`,
      enableEditing: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 20,
      Header: () => <AttachFileIcon />,
      Cell: ({ row }) => (
        <IconButton
          onClick={() => handleButtonClick("attachment", row)}
          disabled={
            plant_people?.some((user) => {
              const userName =
                typeof user === "object" ? user.name || user.email : user;
              return userName === currentUser;
            }) || plantLeads?.includes(currentUser)
              ? false
              : true
          }
        >
          <AttachFileIcon />
        </IconButton>
      ),
    },
    {
      accessorKey: "timesheet",
      header: `Timesheet`,
      enableEditing: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 20,
      Header: () => <PostAddIcon />,
      Cell: ({ row }) => (
        <IconButton onClick={() => handleButtonClick("timesheet", row)}>
          <PostAddIcon />
        </IconButton>
      ),
    },
    {
      accessorKey: "resourcesModal",
      header: `Resources`,
      enableEditing: false,
      enableSorting: false,
      enableColumnFilter: false,
      size: 20,
      Header: () => <ManageAccountsIcon />,
      Cell: ({ row }) => (
        <IconButton onClick={() => handleButtonClick("resources", row)}>
          <ManageAccountsIcon />
        </IconButton>
      ),
    },
  ];

  const mainColumns = [
    {
      id: "uid",
      header: "UID #",
      size: 100,
      enableEditing: false,
      accessorFn: (row) => resolveTaskUidForTask(row, documentList),
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v ? v : "—";
      },
    },
    {
      accessorKey: "id",
      header: `ID`,
      enableEditing: false,
    },

    {
      accessorKey: "taskCode",
      header: fixedType === "fabrication" ? "WBS ID" : `WBS ID`,
      size: 120,
      enableEditing: false,
      Edit: ({ cell, column, row, table }) => {
        const [value, setValue] = React.useState(cell.getValue() || "");

        React.useEffect(() => {
          // Update the row's value cache when value changes
          if (cell.row._valuesCache) {
            cell.row._valuesCache[column.id] = value;
          }
        }, [value, cell, column]);

        return (
          <Autocomplete
            freeSolo
            options={fixedType === "fabrication" ? materials : taskCodeOptions}
            value={value}
            onChange={(event, newValue) => {
              const finalValue =
                typeof newValue === "string"
                  ? newValue
                  : newValue?.inputValue || newValue || "";
              setValue(finalValue);
              if (cell.row._valuesCache) {
                cell.row._valuesCache[column.id] = finalValue;
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setValue(newInputValue);
                if (cell.row._valuesCache) {
                  cell.row._valuesCache[column.id] = newInputValue;
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                size="small"
                fullWidth
                placeholder="Enter or select task code"
              />
            )}
            sx={{ minWidth: 120, width: "100%" }}
          />
        );
      },
    },
    {
      accessorKey: "task",
      header:
        fixedType === "engineering"
          ? `Task`
          : fixedType === "fabrication"
            ? `Scope`
            : `Action Item`,
      enableEditing: false,
      size: 100,
    },
    {
      accessorKey: "startDate",
      header: "Baseline Start",
      Header: () => (
        <Box sx={{ textAlign: "center", lineHeight: 1.2 }}>
          <div>Baseline</div>
          <div>Start</div>
        </Box>
      ),
      size: 100,
      enableColumnFilter: false,
      enableEditing: false,
      Cell: ({ cell }) => (
        <>
          {cell.getValue() ? (
            <div style={{ whiteSpace: "wrap", fontSize: "0.8rem" }}>
              {convertDateString(cell.getValue())}
            </div>
          ) : (
            ""
          )}
        </>
      ),
      muiEditTextFieldProps: ({ cell }) => ({
        type: "date",
        InputLabelProps: { shrink: true },
      }),
    },
    {
      accessorKey: "endDate",
      header: "Baseline Finish",
      Header: () => (
        <Box sx={{ textAlign: "center", lineHeight: 1.2 }}>
          <div>Baseline</div>
          <div>Finish</div>
        </Box>
      ),
      size: 100,
      enableColumnFilter: false,
      enableEditing: false,
      Cell: ({ cell }) => (
        <>
          {cell.getValue() ? (
            <div style={{ whiteSpace: "wrap", fontSize: "0.8rem" }}>
              {convertDateString(cell.getValue())}
            </div>
          ) : (
            ""
          )}
        </>
      ),
      muiEditTextFieldProps: ({ cell }) => ({
        type: "date",
      }),
    },
    {
      accessorKey: "revisedStart",
      header: "Revised Start",
      Header: () => (
        <Box sx={{ textAlign: "center", lineHeight: 1.2 }}>
          <div>Revised</div>
          <div>Start</div>
        </Box>
      ),
      size: 100,
      enableColumnFilter: false,
      enableEditing: false,
      Cell: ({ cell }) => (
        <>
          {cell.getValue() ? (
            <div style={{ whiteSpace: "wrap", fontSize: "0.8rem" }}>
              {convertDateString(cell.getValue())}
            </div>
          ) : (
            ""
          )}
        </>
      ),
      muiEditTextFieldProps: ({ cell }) => ({
        type: "date",
      }),
    },
    {
      accessorKey: "revisedEnd",
      header: "Revised Finish",
      Header: () => (
        <Box sx={{ textAlign: "center", lineHeight: 1.2 }}>
          <div>Revised</div>
          <div>Finish</div>
        </Box>
      ),
      size: 100,
      enableColumnFilter: false,
      enableEditing: false,
      Cell: ({ cell }) => (
        <>
          {cell.getValue() ? (
            <div style={{ whiteSpace: "wrap", fontSize: "0.8rem" }}>
              {convertDateString(cell.getValue())}
            </div>
          ) : (
            ""
          )}
        </>
      ),
      muiEditTextFieldProps: ({ cell }) => ({
        type: "date",
      }),
    },
    isPCSTab
      ? {
          accessorKey: "actualStart",
          header: "Actual Start",
          Header: () => (
            <Box sx={{ textAlign: "center", lineHeight: 1.2 }}>
              <div>Actual</div>
              <div>Start</div>
            </Box>
          ),
          enableEditing: true,
          enableColumnFilter: false,
          enableSorting: false,
          size: 100,
        }
      : {
          accessorKey: "priority",
          header: "Priority",
          enableColumnFilter: false,
          size: 90,
          // Priority column should be editable
          enableEditing: true,
          muiEditTextFieldProps: () => ({
            children: ["High", "Medium", "Low"].map((func) => (
              <MenuItem key={func} value={func}>
                {func}
              </MenuItem>
            )),
            select: true,
          }),
        },

    isPCSTab
      ? {
          accessorKey: "actualFinish",
          header: "Actual Finish",
          Header: () => (
            <Box sx={{ textAlign: "center", lineHeight: 1.2 }}>
              <div>Actual</div>
              <div>Finish</div>
            </Box>
          ),
          enableEditing: false,
          enableColumnFilter: false,
          enableSorting: false,
          size: 100,
        }
      : {
          accessorKey: "task_members",
          header: `Responsible`,
          size: 100,
          enableSorting: false,
          // Responsible column should be editable
          enableEditing: true,
          Cell: ({ cell }) => {
            const raw = cell.getValue();
            const members = Array.isArray(raw)
              ? raw
              : typeof raw === "string" && raw
                ? raw.split(",").map((s) => s.trim())
                : [];
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {members.map((user, index) => (
                  <Chip key={index} label={user} size="small" />
                ))}
              </Box>
            );
          },
          muiEditTextFieldProps: ({ value, onChange }) => {
            // Combine plant people and leads for selection
            const allUsers = [
              ...new Set([
                ...(plant_people || []).map((user) =>
                  typeof user === "object" ? user.name || user.email : user,
                ),
                ...(plantLeads || []),
              ]),
            ];

            return {
              children: allUsers.map((user, index) => (
                <MenuItem key={index} value={user}>
                  {user}
                </MenuItem>
              )),
              select: true,
              SelectProps: {
                multiple: true,
                renderValue: (selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value, index) => (
                      <Chip key={index} label={value} />
                    ))}
                  </Box>
                ),
              },
            };
          },
        },

    {
      accessorKey: "estimatedTime",
      header: fixedType === "procurement" ? "Budged Cost" : "Budget Hours",
      Header: () => (
        <Box sx={{ textAlign: "center", lineHeight: 1.2 }}>
          <div>{fixedType === "procurement" ? "Budget" : "Budget"}</div>
          <div>{fixedType === "procurement" ? "Cost" : "Hours"}</div>
        </Box>
      ),
      enableEditing: true,
      enableColumnFilter: false,
      enableSorting: false,
      size: 80,
    },
    {
      accessorKey: "manhours",
      header: fixedType === "procurement" ? "$" : "Actual Hours",
      Header: () =>
        fixedType === "procurement" ? (
          "$"
        ) : (
          <Box sx={{ textAlign: "center", lineHeight: 1.2 }}>
            <div>Actual</div>
            <div>Hours</div>
          </Box>
        ),
      enableEditing: false,
      enableColumnFilter: false,
      enableSorting: false,
      size: 80,
    },
    buildSiColumn({ onSiChange, siUpdatingIds }),
    buildResourceColumn(resources),
  ];

  if (isPCSTab) {
    return mainColumns;
  } else {
    return [...mainColumns, ...afterPrgressColumns];
  }
};
